import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import qs from "qs";
import { isUndefined, isEmpty } from 'lodash';
import { Form, Input, Row, Col } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import useDocumentTitle from '../../hooks/useDocumentTitle';

import { resetPassword, verifyResetPasswordToken } from '../../api';

const ResetPasswordPage = () => {
  useDocumentTitle("Reset Password")
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [token, setToken] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0)
    const queryStrings = qs.parse(location.search, { ignoreQueryPrefix: true })
    if (!queryStrings.token) {
      navigate("/login", { replace: true })
    } else {
      setToken(queryStrings.token)
      verifyResetPasswordToken(queryStrings.token).then((response) => {
        if (response.data.status != "success") {
          setLinkExpired(true)
        }
      }).catch((e) => setLinkExpired(true))
    }
  }, [])

  const onSubmit = values => {
    const body = {
      password: values.password,
      token: token
    }
    setErrorMessage("");
    resetPassword(body).then(response => {
      setResetSuccess(true)
    }).catch(error => {
      console.log(error)
      setErrorMessage("Something went wrong. Please try again.");
    })
  }

  const renderContent = () => {
    if (linkExpired) {
      return renderLinkExpired()
    } else if (resetSuccess) {
      return renderResetSuccess()
    } else {
      return renderPage()
    }
  }

  const renderError = () => {
    if (!isEmpty(errorMessage)) {
      return <div className="login-error">{errorMessage}</div>;
    }
    return null;
  }

  const renderLinkExpired = () => {
    return (
      <div className="center-content-box">
        <div className="center-content-box--title">Reset Password</div>
        <div className="center-content-box--subtitle">This link is either invalid or expired. Please request a new link by clicking the button below.</div>
        <div className="center-content-box--form">
          <button className="primary-button" onClick={() => navigate("/forgot-password")}>Resend Link</button>
            <div className="center-content-box--link-container">
              <Link to="/login" className="center-content-box--link">Sign In</Link>
            </div>
        </div>
      </div>
    )
  }

  const renderResetSuccess = () => {
    return (
      <div className="center-content-box">
        <div className="center-content-box--title">Success!</div>
        <div className="center-content-box--subtitle">You have successfully reset your password! Click the button below to sign in.</div>
        <div className="center-content-box--form">
          <button className="primary-button" onClick={() => navigate("/login")}>Sign In</button>
        </div>
      </div>
    )
  }

  const renderPage = () => {
    return (
      <div className="center-content-box">
        <div className="center-content-box--title">Reset Password</div>
        <div className="center-content-box--subtitle">Enter a new password below to continue.</div>
        <div className="center-content-box--form">
          <Form layout="vertical" name="admin-login" onFinish={onSubmit}>
            <Row gutter={[20,20]}>
              <Col xs={24}>
                <Form.Item
                  name={"password"}
                  rules={[
                    { required: true, message: `Password is required!`, validateTrigger: "onBlur" }
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="New Password"
                    iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={"confirm_password"}
                  dependencies={['password']}
                  rules={[
                    { required: true, message: `Confirm Password is required!` },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('The two passwords that you entered do not match!'));
                      },
                    })
                  ]}
                >
                  <Input.Password
                    size="large"
                    placeholder="Confirm Password"
                    iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  />
                </Form.Item>
              </Col>
            </Row>
            { renderError() }
            <button className="primary-button" type="submit">Reset Password</button>
            <div className="center-content-box--link-container">
              <Link to="/login" className="center-content-box--link">Sign In</Link>
            </div>
          </Form>
        </div>
      </div>
    )
  }

  return (
    <div className="center-content-page">
      { renderContent() }
    </div>
  );
}

export default ResetPasswordPage;
